import React, { useState, useEffect } from "react";
import { t } from "@lingui/macro";

const RoomSelectionModal = ({
  availableRooms,
  onRoomSelection,
  onBack,
  startDate,
  endDate,
}) => {
  const [selectedRooms, setSelectedRooms] = useState({});
  const [guestCount, setGuestCount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  // --------------------------------------------------
  // HELPERS
  // --------------------------------------------------
  const calculateNights = (startDate, endDate) => {
    const timeDiff = endDate.getTime() - startDate.getTime();
    // ⬅️ Zawsze liczba całkowita, bez błędów typu 2.00000003
    const days = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return days;
  };

  // --------------------------------------------------
  // TOTAL PRICE CALCULATION
  // --------------------------------------------------
  useEffect(() => {
    const calculateTotalPrice = () => {
      let total = 0;
      const nights = calculateNights(startDate, endDate);

      console.log(`Number of nights: ${nights}`);

      Object.entries(selectedRooms).forEach(([roomId, amount]) => {
        const room = availableRooms.find((room) => room.type === roomId);

        console.log(`Selected room: ${roomId}, Amount: ${amount}`);
        console.log(`Room details:`, room);

        if (room && amount > 0) {
          let price = 0;

          if (room.priceWithPromotion?.hasPromotion) {
            console.log(
              `Room ${roomId} has promotion. Final price: ${room.priceWithPromotion.finalPrice}`
            );
            price = room.priceWithPromotion.finalPrice;
          } else {
            console.log(
              `Room ${roomId} has no promotion. Price: ${room.priceWithPromotion.originalPrice}`
            );
            price = room.priceWithPromotion.originalPrice;
          }

          if (!isNaN(price) && !isNaN(nights)) {
            total += price * amount * nights;
          } else {
            console.log(
              `Invalid price or nights for room: ${roomId}. Price: ${price}, Nights: ${nights}`
            );
          }
        }
      });

      console.log(`Total calculated price (raw): ${total}`);

      // ⬅️ Zawsze 2 miejsca po przecinku i Number
      const roundedTotal = Number(total.toFixed(2));
      console.log(`Total calculated price (rounded): ${roundedTotal}`);

      setTotalPrice(roundedTotal);
    };

    calculateTotalPrice();
  }, [selectedRooms, availableRooms, startDate, endDate]);

  // --------------------------------------------------
  // HANDLERS
  // --------------------------------------------------
  const handleRoomChange = (roomId, value) => {
    console.log(`Room changed: ${roomId}, New value: ${value}`);
    setSelectedRooms((prev) => ({
      ...prev,
      [roomId]: parseInt(value, 10) || 0,
    }));
  };

  const handleSubmit = () => {
    if (guestCount <= 0) {
      alert(t`Please enter a valid number of guests.`);
      return;
    }

    const totalCapacity = Object.entries(selectedRooms).reduce(
      (total, [roomId, amount]) => {
        const room = availableRooms.find((room) => room.type === roomId);
        return total + (room ? room.capacity * amount : 0);
      },
      0
    );

    console.log(
      `Total capacity: ${totalCapacity}, Guest count: ${guestCount}`
    );

    if (totalCapacity < guestCount) {
      alert(
        t`Selected rooms cannot accommodate ${guestCount} guests. Please adjust your selection.`
      );
      return;
    }

    console.log("Final total price sent to parent:", totalPrice);

    // ⬅️ zawsze Number, nie string
    onRoomSelection(selectedRooms, Number(totalPrice), guestCount);
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t`Select Rooms`}</h2>

        <div className="input-container">
          <label>{t`Number of Guests:`}</label>
          <input
            type="number"
            min="1"
            value={guestCount}
            onChange={(e) =>
              setGuestCount(parseInt(e.target.value, 10) || 0)
            }
            style={{ marginBottom: "16px" }}
          />
        </div>

        <div className="room-list">
          {availableRooms.map((room, index) => {
            const imagePath = `/assets/images/rooms/${room.imagePath}`;

            return (
              <div key={index} className="room-card">
                <h3>{room.type}</h3>

                <img
                  src={imagePath}
                  alt={room.type}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/images/rooms/default.webp";
                  }}
                />

                <p>
                  <strong>{t`Capacity:`}</strong> {room.capacity}
                </p>
                <p>
                  <strong>{t`Beds:`}</strong> {room.beds}
                </p>
                <p>
                  <strong>{t`Description:`}</strong> {room.description}
                </p>
                <p>
                  <strong>{t`Available:`}</strong> {room.availableCount}
                </p>

                {/* PRICE SECTION */}
                <div className="price-section">
                  {room.priceWithPromotion &&
                  room.priceWithPromotion.hasPromotion ? (
                    <div>
                      <span className="original-price">
                        <strong>
                          {room.priceWithPromotion.originalPrice}
                        </strong>{" "}
                        {room.priceWithPromotion.currency}
                      </span>
                      <span className="final-price">
                        <strong>
                          {room.priceWithPromotion.finalPrice}
                        </strong>{" "}
                        {room.priceWithPromotion.currency}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <span>
                        <strong>
                          {room.priceWithPromotion
                            ? room.priceWithPromotion.originalPrice
                            : room.price}
                        </strong>{" "}
                        {room.priceWithPromotion
                          ? room.priceWithPromotion.currency
                          : ""}
                      </span>
                    </div>
                  )}
                </div>

                <div className="input-container">
                  <label>{t`Number to reserve:`}</label>
                  <input
                    type="number"
                    min="0"
                    max={room.availableCount}
                    onChange={(e) =>
                      handleRoomChange(room.type, e.target.value)
                    }
                    placeholder={t`0`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="total-price">
          <h3>
            <strong>{t`Total Price:`}</strong>{" "}
            {isNaN(totalPrice) ? 0 : totalPrice} MXN
          </h3>
        </div>

        <div className="modal-actions">
          <button className="add-new-btn" onClick={onBack}>
            {t`Back`}
          </button>
          <button className="add-new-btn" onClick={handleSubmit}>
            {t`Next`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomSelectionModal;
